import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Delete,
  Warning,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { removeItem } from '../../store/slices/inventorySlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { InventoryItem } from '../../types/models';

const ConfirmDeleteDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = dialog.data as InventoryItem;
  const isOpen = dialog.open && dialog.type === 'confirm-delete';

  const handleClose = () => {
    dispatch(closeDialog());
    setError(null);
  };

  const handleConfirmDelete = async () => {
    if (!item?.id) {
      setError('Invalid item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await FirestoreService.deleteInventoryItem(item.id);

      // Update local state immediately for optimistic UI
      dispatch(removeItem(item.id));

      dispatch(addNotification({
        message: `${item.itemName} deleted successfully`,
        type: 'success',
      }));

      handleClose();
    } catch (error: any) {
      console.error('Error deleting item:', error);
      setError(error.message || 'Failed to delete item');
      dispatch(addNotification({
        message: `Failed to delete ${item.itemName}`,
        type: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  if (!item) {
    return null;
  }

  const hasCheckoutHistory = item.checkOutHistory && item.checkOutHistory.length > 0;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Warning color="warning" />
          <Typography variant="h6">Confirm Delete</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" paragraph>
          Are you sure you want to delete this item? This action cannot be undone.
        </Typography>

        {/* Item Details */}
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {item.itemName}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={item.checkedOut ? 'Checked Out' : 'Available'}
              color={item.checkedOut ? 'error' : 'success'}
              icon={item.checkedOut ? <Cancel /> : <CheckCircle />}
              size="small"
            />
            
            {item.itemGroup && (
              <Chip
                label={item.itemGroup}
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Item ID: {item.itemID}
          </Typography>
          
          {item.itemQRFileName && (
            <Typography variant="body2" color="text.secondary">
              QR Code: {item.itemQRFileName}
            </Typography>
          )}
        </Box>

        {/* Warning for checked out items */}
        {item.checkedOut && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This item is currently checked out. 
              Deleting it will remove all checkout history and may cause confusion 
              for the person who has it checked out.
            </Typography>
          </Alert>
        )}

        {/* Warning for items with history */}
        {hasCheckoutHistory && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This item has checkout history ({item.checkOutHistory.length} events). 
              Deleting it will permanently remove this history.
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          This will permanently delete the item and all associated data from the system.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Delete />}
        >
          {loading ? 'Deleting...' : 'Delete Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;